import { DriveStep, driver } from 'driver.js';
import { useEffect, useRef } from 'react';
import { UserOnboardingStepId } from '../types';
import { apiError } from '../util/notify';
import {
  areTourTargetsReady,
  clearQueuedOnboardingTour,
  getQueuedOnboardingTour,
} from '../services/onboardingTourService';

interface UseOnboardingDriverTourOptions {
  stepId: UserOnboardingStepId;
  steps: DriveStep[];
  isEnabled?: boolean;
}

export const useOnboardingDriverTour = ({
  stepId,
  steps,
  isEnabled = true,
}: UseOnboardingDriverTourOptions) => {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  useEffect(() => {
    if (!isEnabled || !steps.length) {
      return undefined;
    }

    if (getQueuedOnboardingTour() !== stepId) {
      return undefined;
    }

    let cancelled = false;
    let pollTimer: number | null = null;
    let timeoutTimer: number | null = null;

    const clearTimers = () => {
      if (pollTimer) {
        window.clearInterval(pollTimer);
      }

      if (timeoutTimer) {
        window.clearTimeout(timeoutTimer);
      }
    };

    const startTour = () => {
      if (cancelled || driverRef.current) {
        return;
      }

      driverRef.current = driver({
        allowClose: true,
        animate: true,
        doneBtnText: 'Finish guide',
        nextBtnText: 'Next',
        overlayColor: '#081217',
        overlayOpacity: 0.72,
        popoverClass: 'smartpos-driver-popover',
        prevBtnText: 'Back',
        showProgress: true,
        smoothScroll: true,
        stagePadding: 12,
        stageRadius: 18,
        steps,
        onDestroyed: () => {
          clearQueuedOnboardingTour();
          driverRef.current = null;
        },
      });

      driverRef.current.drive();
    };

    const checkAndStartTour = () => {
      if (!areTourTargetsReady(steps)) {
        return;
      }

      clearTimers();
      startTour();
    };

    checkAndStartTour();

    if (!driverRef.current) {
      pollTimer = window.setInterval(checkAndStartTour, 250);
      timeoutTimer = window.setTimeout(() => {
        clearTimers();
        clearQueuedOnboardingTour();

        if (!cancelled) {
          apiError(
            'We could not find all the guided tour targets on this page. Please refresh and try again.',
          );
        }
      }, 10000);
    }

    return () => {
      cancelled = true;
      clearTimers();

      if (driverRef.current?.isActive()) {
        driverRef.current.destroy();
      }

      driverRef.current = null;
    };
  }, [isEnabled, stepId, steps]);
};
